import { useAuth0 as useAuth0SDK } from '@auth0/auth0-react';
import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setAuth0Client, clearAuth0Token } from '../utils/api';
import { identifyUser, clearUserIdentification } from '../utils/logrocket';

export const useAuth0 = () => {
  const auth0 = useAuth0SDK();
  const queryClient = useQueryClient();
  
  const sessionData = auth0.isAuthenticated ? {
    user: auth0.user,
    organization: null
  } : null;
  
  useEffect(() => {
    console.log('useAuth0 effect:', {
      isAuthenticated: auth0.isAuthenticated,
      isLoading: auth0.isLoading,
      user: auth0.user?.email,
      sessionData: sessionData?.user?.email
    });
    
    setAuth0Client(auth0);
    
    if (auth0.isAuthenticated && auth0.user && sessionData?.user) {
      const logRocketUser = {
        id: auth0.user.sub || '',
        email: auth0.user.email || '',
        name: auth0.user.name,
        organizationId: auth0.user.org_id || '',
        organizationName: auth0.user.org_name || '',
        organizationSlug: auth0.user.org_slug || '',
        role: (auth0.user.role as 'admin' | 'member') || 'member'
      };
      
      if (logRocketUser.id && logRocketUser.email) {
        identifyUser(logRocketUser);
      }
    } else if (!auth0.isAuthenticated && !auth0.isLoading) {
      clearAuth0Token();
      clearUserIdentification();
      queryClient.clear();
    }
  }, [auth0.isAuthenticated, auth0.isLoading, auth0, sessionData, queryClient]);
  
  const loginWithRedirect = useCallback(async (options: any = {}) => {
    const loginOptions = {
      ...options,
      appState: {
        returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        ...options.appState,
      },
    };

    return auth0.loginWithRedirect(loginOptions);
  }, [auth0]);

  const logout = useCallback((options: any = {}) => {
    clearAuth0Token();
    clearUserIdentification();
    
    const logoutOptions = {
      logoutParams: {
        returnTo: window.location.origin,
      },
      ...options,
    };

    return auth0.logout(logoutOptions);
  }, [auth0]);

  const getAccessToken = useCallback(async () => {
    if (auth0.isAuthenticated) {
      return await auth0.getAccessTokenSilently();
    }
    throw new Error('No Auth0 token available');
  }, [auth0]);

  return {
    ...auth0,
    
    loginWithRedirect,
    logout,
    getAccessToken,
    
    user: sessionData?.user || null,
    organization: sessionData?.organization || null,
    
    isLoading: auth0.isLoading,
        
    isCreatingOrganization: false,
  };
};
